import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ApiService from '../../services/api';
import { persistAdminPortalSession } from '../../utils/adminPortalSession';
import { clearClientAuthSession } from '../../utils/clearClientAuthSession';
import { hasClientAuthSession, getClientAccessToken } from '../../utils/sessionAuth';

interface AdminWriteRouteProps {
  children: React.ReactNode;
}

/** Restricts routes to platform administrators (not credentialed reviewers). */
const AdminWriteRoute: React.FC<AdminWriteRouteProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [isAdminWrite, setIsAdminWrite] = useState(false);
  const [sessionInvalid, setSessionInvalid] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!hasClientAuthSession()) {
        setIsAdminWrite(false);
        setSessionInvalid(true);
        setChecking(false);
        return;
      }
      try {
        const response = await ApiService.request('/auth/me');
        const user = response?.data?.user;
        const isAdmin = user?.role === 'admin';
        const token = getClientAccessToken();
        if (isAdmin && user) {
          persistAdminPortalSession(user, token);
        }
        setIsAdminWrite(isAdmin);
        setSessionInvalid(false);
      } catch {
        clearClientAuthSession();
        setIsAdminWrite(false);
        setSessionInvalid(true);
      } finally {
        setChecking(false);
      }
    };
    check();
  }, []);

  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdminWrite) {
    // Expired/missing session → login. Authenticated non-admin (e.g. reviewer) → admin home.
    return <Navigate to={sessionInvalid ? '/login' : '/admin/dashboard'} replace />;
  }

  return <>{children}</>;
};

export default AdminWriteRoute;
